import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Users, Heart, MessageCircle, Send, Trash2, Loader2, Trophy, TrendingUp,
  Crown, Tag, ArrowUp, ArrowDown, Sparkles, Flame,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANK_COLOR = { Diamond: "#7cc7ff", Platinum: "#8fd3ff", Gold: "#e6c25a", Silver: "#c0c8d4", Bronze: "#b08d57" };
const money = (n) => `${n < 0 ? "-" : "+"}$${Math.abs(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Avatar = ({ name, picture, size = 40 }) =>
  picture ? (
    <img src={picture} alt={name} className="rounded-full object-cover border border-[color:var(--ws-border-strong)]" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full flex items-center justify-center font-semibold text-white shrink-0" style={{ width: size, height: size, background: "linear-gradient(135deg,#2563eb,#38bdf8)", fontSize: size * 0.4 }}>
      {(name || "T")[0].toUpperCase()}
    </div>
  );

const RankBadge = ({ rank }) => (
  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1"
    style={{ color: RANK_COLOR[rank], background: "rgba(255,255,255,0.06)", border: `1px solid ${RANK_COLOR[rank]}33` }}>
    <Crown className="w-2.5 h-2.5" /> {rank}
  </span>
);

const TradeTag = ({ trade }) => {
  if (!trade?.symbol) return null;
  const buy = (trade.side || "").toUpperCase() !== "SELL";
  const up = (trade.pnl ?? 0) >= 0;
  return (
    <div className="ws-price-cell inline-flex items-center gap-3 px-3 py-2 rounded-xl mb-3" data-testid="post-trade-tag">
      <span className="ws-heading font-bold text-white text-[13px]">{trade.symbol}</span>
      <span className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${buy ? "ws-up" : "ws-down"}`}>
        {buy ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{buy ? "BUY" : "SELL"}
      </span>
      {trade.pnl != null && <span className={`text-[13px] font-bold ${up ? "ws-up" : "ws-down"}`}>{money(trade.pnl)}</span>}
    </div>
  );
};

const CommentThread = ({ postId, me, onCount, canComment }) => {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try { const d = await (await fetch(`${API}/community/posts/${postId}/comments`, { credentials: "include" })).json(); setComments(d.comments || []); }
      catch { setComments([]); }
    })();
  }, [postId]);
  const add = async () => {
    const t = text.trim(); if (!t) return;
    setBusy(true);
    try {
      const d = await (await fetch(`${API}/community/posts/${postId}/comment`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: t }),
      })).json();
      setComments((c) => [...(c || []), d.comment]); setText(""); onCount?.(d.comment_count);
    } catch { toast.error("Could not comment."); }
    setBusy(false);
  };
  return (
    <div className="mt-3 pt-3 border-t border-[color:var(--ws-border)]" data-testid={`comments-${postId}`}>
      {comments === null ? (
        <div className="text-center py-2 text-[color:var(--ws-muted)]"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : (
        <div className="space-y-3 mb-3">
          {comments.length === 0 && <div className="text-[12px] text-[color:var(--ws-muted)]">No comments yet — be the first.</div>}
          {comments.map((c) => (
            <div key={c.comment_id} className="flex gap-2.5">
              <Avatar name={c.name} picture={c.picture} size={28} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="text-[12px] font-semibold text-white">{c.name}</span><span className="text-[10px] text-[color:var(--ws-muted)]">{ago(c.created_at)}</span></div>
                <div className="text-[13px] text-[color:var(--ws-text)] opacity-90">{c.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {canComment ? (
          <>
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Write a comment…" data-testid={`comment-input-${postId}`}
              className="flex-1 bg-[rgba(255,255,255,0.04)] border border-[color:var(--ws-border)] rounded-lg px-3 py-2 text-[13px] text-[color:var(--ws-text)] placeholder:text-[color:var(--ws-muted)] outline-none focus:border-[color:var(--ws-blue)]" />
            <button onClick={add} disabled={busy} data-testid={`comment-send-${postId}`} className="ws-btn-primary px-3 py-2 rounded-lg text-[12px] font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </>
        ) : (
          <div className="text-[12px] text-[color:var(--ws-muted)] italic" data-testid={`comment-locked-${postId}`}>Comments are posted by the Wealth Secret team only.</div>
        )}
      </div>
    </div>
  );
};

const PostCard = ({ p, onLike, onReact, onDelete, canComment }) => {
  const [open, setOpen] = useState(false);
  const [cc, setCc] = useState(p.comment_count);
  const RxBtn = ({ type, emoji }) => {
    const on = p.my_reactions?.includes(type);
    return (
      <button onClick={() => onReact(p, type)} data-testid={`react-${type}-${p.post_id}`}
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-colors ${on ? "bg-[rgba(59,130,246,0.16)] text-white" : "hover:text-white"}`}>
        <span className="text-[14px] leading-none">{emoji}</span> {p.reactions?.[type] || 0}
      </button>
    );
  };
  return (
    <div className="ws-panel ws-panel-hover p-5" data-testid={`post-${p.post_id}`}>
      <div className="flex items-center gap-3 mb-3">
        <Avatar name={p.name} picture={p.picture} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="ws-heading font-semibold text-white text-[14px]">{p.name}</span>
            <RankBadge rank={p.rank} />
          </div>
          <span className="text-[11px] text-[color:var(--ws-muted)]">{ago(p.created_at)}</span>
        </div>
        {p.is_mine && (
          <button onClick={() => onDelete(p.post_id)} data-testid={`delete-${p.post_id}`} title="Delete"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[color:var(--ws-muted)] hover:text-[color:var(--ws-red)] hover:bg-[rgba(248,113,113,0.1)] transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <TradeTag trade={p.trade} />
      {p.image && <img src={p.image.startsWith("http") ? p.image : `${process.env.REACT_APP_BACKEND_URL}${p.image}`} alt="chart" className="rounded-xl mb-3 w-full max-w-[420px] border border-[color:var(--ws-border)]" loading="lazy" />}
      {p.text && <p className="text-[14px] text-[color:var(--ws-text)] opacity-90 mb-3 whitespace-pre-wrap">{p.text}</p>}
      <div className="flex items-center gap-5 text-[13px] text-[color:var(--ws-muted)]">
        <button onClick={() => onLike(p)} data-testid={`like-${p.post_id}`}
          className={`flex items-center gap-1.5 transition-colors ${p.liked_by_me ? "ws-down" : "hover:text-[color:var(--ws-blue-bright)]"}`}>
          <Heart className="w-4 h-4" fill={p.liked_by_me ? "currentColor" : "none"} /> {p.like_count}
        </button>
        <button onClick={() => setOpen((o) => !o)} data-testid={`comment-toggle-${p.post_id}`}
          className={`flex items-center gap-1.5 transition-colors ${open ? "text-[color:var(--ws-blue-bright)]" : "hover:text-[color:var(--ws-blue-bright)]"}`}>
          <MessageCircle className="w-4 h-4" /> {cc}
        </button>
        <div className="flex items-center gap-1 ml-auto"><RxBtn type="fire" emoji="🔥" /><RxBtn type="target" emoji="🎯" /></div>
      </div>
      {open && <CommentThread postId={p.post_id} onCount={setCc} canComment={canComment} />}
    </div>
  );
};

const Community = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("latest");
  const [draft, setDraft] = useState("");
  const [showTrade, setShowTrade] = useState(false);
  const [trade, setTrade] = useState({ symbol: "", side: "BUY", pnl: "" });
  const [posting, setPosting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pd, ld] = await Promise.all([
        fetch(`${API}/community/posts?sort=${sort}`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/community/leaderboard`, { credentials: "include" }).then((r) => r.json()),
      ]);
      setPosts(pd.posts || []);
      setIsAdmin(!!pd.is_admin);
      setLeaders(ld.leaders || []);
    } catch { /* */ }
    setLoading(false);
  }, [sort]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = draft.trim();
    const tr = showTrade && trade.symbol.trim() ? { symbol: trade.symbol.trim().toUpperCase(), side: trade.side, pnl: trade.pnl === "" ? null : Number(trade.pnl) } : null;
    if (!t && !tr) { toast.error("Write something or attach a trade."); return; }
    setPosting(true);
    try {
      const d = await (await fetch(`${API}/community/posts`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: t, trade: tr }),
      })).json();
      if (d.post) { setPosts((ps) => [d.post, ...ps]); setDraft(""); setShowTrade(false); setTrade({ symbol: "", side: "BUY", pnl: "" }); toast.success("Posted to the community!"); load(); }
    } catch { toast.error("Could not post."); }
    setPosting(false);
  };

  const like = async (p) => {
    setPosts((ps) => ps.map((x) => x.post_id === p.post_id ? { ...x, liked_by_me: !x.liked_by_me, like_count: x.like_count + (x.liked_by_me ? -1 : 1) } : x));
    try { await fetch(`${API}/community/posts/${p.post_id}/like`, { method: "POST", credentials: "include" }); }
    catch { load(); }
  };

  const react = async (p, type) => {
    setPosts((ps) => ps.map((x) => {
      if (x.post_id !== p.post_id) return x;
      const has = x.my_reactions?.includes(type);
      const my = has ? x.my_reactions.filter((t) => t !== type) : [...(x.my_reactions || []), type];
      return { ...x, my_reactions: my, reactions: { ...x.reactions, [type]: (x.reactions?.[type] || 0) + (has ? -1 : 1) } };
    }));
    try { await fetch(`${API}/community/posts/${p.post_id}/react`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type }) }); }
    catch { load(); }
  };

  const del = async (id) => {
    setPosts((ps) => ps.filter((x) => x.post_id !== id));
    try { await fetch(`${API}/community/posts/${id}`, { method: "DELETE", credentials: "include" }); toast.success("Post deleted."); load(); }
    catch { toast.error("Could not delete."); load(); }
  };

  return (
    <div className="max-w-[1180px] mx-auto px-8 py-8">
      <div className="flex items-center gap-3 mb-2 ws-fade-up">
        <div className="ws-icon-chip w-9 h-9"><Users className="w-5 h-5" /></div>
        <span className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Traders Who Level Up Together</span>
      </div>
      <h1 className="ws-heading text-[40px] font-bold mb-6 ws-title-gradient ws-fade-up" style={{ animationDelay: "40ms" }}>Community</h1>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Feed */}
        <div>
          {/* Composer */}
          {isAdmin ? (
          <div className="ws-panel p-4 mb-4 ws-fade-up" style={{ animationDelay: "60ms" }} data-testid="composer">
            <div className="flex gap-3">
              <Avatar name={user?.name} picture={user?.picture} />
              <div className="flex-1">
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} data-testid="composer-input"
                  placeholder="Share a win, a lesson, or a setup…"
                  className="w-full bg-transparent resize-none outline-none text-[14px] text-[color:var(--ws-text)] placeholder:text-[color:var(--ws-muted)]" />
                {showTrade && (
                  <div className="flex items-center gap-2 mb-2 flex-wrap ws-fade-up">
                    <input value={trade.symbol} onChange={(e) => setTrade((t) => ({ ...t, symbol: e.target.value }))} placeholder="Symbol (e.g. XAUUSD)" data-testid="trade-symbol"
                      className="bg-[rgba(255,255,255,0.04)] border border-[color:var(--ws-border)] rounded-lg px-3 py-1.5 text-[13px] text-white outline-none focus:border-[color:var(--ws-blue)] w-[160px]" />
                    <div className="ws-seg flex items-center gap-1">
                      {["BUY", "SELL"].map((s) => (
                        <button key={s} onClick={() => setTrade((t) => ({ ...t, side: s }))} data-testid={`trade-side-${s}`}
                          className={`px-3 py-1.5 rounded-md text-[12px] font-semibold ${trade.side === s ? (s === "BUY" ? "bg-[rgba(52,211,153,0.2)] ws-up" : "bg-[rgba(248,113,113,0.2)] ws-down") : "text-[color:var(--ws-muted)]"}`}>{s}</button>
                      ))}
                    </div>
                    <input value={trade.pnl} onChange={(e) => setTrade((t) => ({ ...t, pnl: e.target.value }))} placeholder="P&L $" type="number" data-testid="trade-pnl"
                      className="bg-[rgba(255,255,255,0.04)] border border-[color:var(--ws-border)] rounded-lg px-3 py-1.5 text-[13px] text-white outline-none focus:border-[color:var(--ws-blue)] w-[110px]" />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <button onClick={() => setShowTrade((s) => !s)} data-testid="attach-trade-btn"
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${showTrade ? "ws-btn-primary" : "ws-btn-ghost"}`}>
                    <Tag className="w-3.5 h-3.5" /> {showTrade ? "Trade attached" : "Attach trade"}
                  </button>
                  <button onClick={submit} disabled={posting} data-testid="post-submit" className="ws-btn-primary px-4 py-2 rounded-lg text-[13px] font-semibold inline-flex items-center gap-2">
                    {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
                  </button>
                </div>
              </div>
            </div>
          </div>
          ) : (
            <div className="ws-panel p-4 mb-4 ws-fade-up flex items-center gap-3" style={{ animationDelay: "60ms" }} data-testid="composer-locked">
              <div className="ws-icon-chip w-9 h-9"><Users className="w-5 h-5" /></div>
              <div>
                <div className="ws-heading font-semibold text-white text-[14px]">This is a broadcast community</div>
                <div className="text-[12px] text-[color:var(--ws-muted)]">Only the Wealth Secret team posts here. Like the calls and setups that help you most.</div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            {[["latest", "Latest", TrendingUp], ["top", "Top", Flame]].map(([v, l, Ic]) => (
              <button key={v} onClick={() => setSort(v)} data-testid={`sort-${v}`}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${sort === v ? "ws-btn-primary" : "ws-btn-ghost"}`}>
                <Ic className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>

          {/* Posts */}
          {loading ? (
            <div className="ws-panel p-10 text-center text-[color:var(--ws-muted)]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : (
            <div className="space-y-4" data-testid="feed">
              {posts.map((p) => <PostCard key={p.post_id} p={p} onLike={like} onReact={react} onDelete={del} canComment={isAdmin} />)}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="ws-fade-up" style={{ animationDelay: "100ms" }}>
          <div className="ws-panel p-5 sticky top-6" data-testid="leaderboard">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-4 h-4 text-[#e6c25a]" />
              <h2 className="ws-heading font-semibold text-[15px]">Top Traders</h2>
            </div>
            <div className="space-y-3">
              {leaders.map((r, i) => (
                <div key={r.user_id} className="flex items-center gap-3" data-testid={`leader-${i}`}>
                  <span className="w-5 text-center text-[13px] font-bold" style={{ color: i < 3 ? "#e6c25a" : "var(--ws-muted)" }}>{i + 1}</span>
                  <Avatar name={r.name} picture={r.picture} size={34} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-white truncate">{r.name}</div>
                    <div className="flex items-center gap-2 text-[10px] text-[color:var(--ws-muted)]">
                      <span style={{ color: RANK_COLOR[r.rank] }}>{r.rank}</span> · {r.posts} posts
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-bold ws-up inline-flex items-center gap-1"><Heart className="w-3 h-3" fill="currentColor" /> {r.likes}</div>
                  </div>
                </div>
              ))}
              {leaders.length === 0 && <div className="text-[12px] text-[color:var(--ws-muted)]">No traders yet.</div>}
            </div>
            <div className="ws-divider my-4" />
            <div className="flex items-start gap-2 text-[11px] text-[color:var(--ws-muted)]">
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--ws-blue-bright)] shrink-0 mt-0.5" />
              Earn likes to climb Bronze → Diamond. Share real setups and lessons.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;
